import { WEIGHT_MAX, WEIGHT_SECONDARY } from '../data/positions.ts';

/**
 * The palette the screen is drawn with. Values mirror the Modernist tokens in
 * `src/styles/modernist.css`; they live here as well because most of this screen
 * picks colours conditionally from data (violation vs clean, foreign vs local).
 */
export const C = {
  /** Ink — primary text and the heavy 2px rules. */
  ink: '#201e1d',
  /** Softer ink for secondary copy. */
  ink2: '#3a3735',
  /** Page ground. */
  bg: '#f3f2f2',
  /** Raised surface — panel headers, filter bars. */
  surface: '#eae9e9',
  /** Hairline rule between cells. */
  line: '#c9c6c4',
  /** Muted label text. */
  muted: '#6b6663',
  /** Accent — the single red the system runs on. */
  red: '#ec3013',
  /** Deep accent, dark enough for paragraph-size text on the light ground. */
  deep: '#a81f0a',
  /** Accent tint — selected rows, completed exits. */
  tint: '#f8cabf',
  /** Club gold, used only on the dark header and in the preview overlay. */
  gold: '#e8b400',
} as const;

/**
 * The drag-fit scale. While a player is being dragged every pitch slot is repainted to
 * say how well *he* covers it, read straight off `positionWeights`: green from 4 up,
 * gold at 2–3, red below. Green and gold are the only colours outside the Modernist
 * palette — the scale has to be legible at a glance, which two shades of one accent
 * cannot manage.
 *
 * `wash` is the slot fill and `line` its border, so an empty slot still carries the
 * signal. A weight of 0 is red like any other miss but drawn dashed, because it is the
 * one band that also refuses the drop.
 */
export const FIT = {
  /** 4–5 — a position he really plays. */
  natural: { line: '#1c7a37', wash: 'rgba(28,122,55,0.18)' },
  /** 2–3 — he can cover it. */
  cover: { line: '#b58600', wash: 'rgba(232,180,0,0.22)' },
  /** 1 — emergency only. */
  stretch: { line: '#ec3013', wash: 'rgba(236,48,19,0.16)' },
  /** 0 — cannot play there; the drop is refused. */
  blocked: { line: '#ec3013', wash: 'rgba(236,48,19,0.07)' },
} as const;

/**
 * Which band of the scale a weight falls in. The pitch's drag preview and the depth
 * chart's chips both read it, so a player shown gold in one is gold in the other.
 */
export function fitBand(weight: number) {
  if (weight >= WEIGHT_MAX - 1) return { ...FIT.natural, weight, dashed: false };
  if (weight >= WEIGHT_SECONDARY) return { ...FIT.cover, weight, dashed: false };
  if (weight >= 1) return { ...FIT.stretch, weight, dashed: false };
  return { ...FIT.blocked, weight, dashed: true };
}

/** Extra greys used inside the dark header bar. */
export const HEADER = {
  bg: '#201e1d',
  fg: '#f3f2f2',
  divider: '#4a4644',
  muted: '#a9a4a1',
  outline: '#6b6663',
} as const;

/** Colours specific to the full-screen squad poster. */
export const POSTER = {
  paper: '#fdfcfa',
  gold: '#f5a623',
  ink: '#8d1218',
  shadow: 'rgba(60,4,8,0.28)',
  shadowSoft: 'rgba(60,4,8,0.24)',
} as const;
